import { useCallback, useEffect, useRef, useState } from 'react'
import { Room, RoomEvent, Track } from 'livekit-client'
import type { RemoteTrack, RemoteTrackPublication, RemoteParticipant, LocalTrack } from 'livekit-client'
import { apiEndpoint } from '../apollo/client'
import { getGuestId } from './guest'

// React port of the buyer side of the LiveShop demo's Stimulus
// `room_controller.js` + `event_bus_controller.js`. Same hard-won
// patterns, just driven by hooks instead of the Stimulus bus:
//   - mint a session token from POST /api/voice_sessions
//   - own a livekit-client Room, gate remote tracks to AGENT identities
//   - grant mic permission ONCE + pick a real (non-virtual) device, then
//     mute/unmute a persisted track for push-to-talk (no re-prompt, no
//     accidentally grabbing the Teams/Zoom virtual mic)
//   - route versioned data envelopes → assistant state + transcript
//   - push-to-talk that won't open the mic while the avatar is speaking
//
// We use the imperative livekit-client Room (NOT <LiveKitRoom> from
// @livekit/components-react) specifically because we need the
// agent-identity gate on TrackSubscribed — the demo learned the hard
// way that Simli publishes video under a *different* identity than the
// LLM agent, and a naive auto-subscribe renders the wrong tracks.

export type AvatarPhase = 'idle' | 'connecting' | 'live' | 'ended' | 'error'
export type AssistantState = 'idle' | 'listening' | 'thinking' | 'speaking'
export interface TranscriptEntry { speaker: 'you' | 'assistant'; text: string }

export interface UseVoiceAvatar {
  phase: AvatarPhase
  assistantState: AssistantState
  transcript: TranscriptEntry[]
  micActive: boolean
  /** True once an agent video track is attached (Simli on). False in
   *  voice-only / text_only mode, so the UI can show a voice indicator. */
  hasVideo: boolean
  error: string | null
  start: () => Promise<void>
  stop: () => Promise<void>
  pttStart: () => Promise<void>
  pttStop: () => Promise<void>
  avatarRef: React.RefObject<HTMLDivElement>
}

interface Args {
  productId: string
  storeId: string
  /** Auth JWT for a signed-in buyer; null/undefined → anonymous guest. */
  token?: string | null
  /** Called when the agent reports it changed the basket (cart_updated),
   *  so the cart UI can refetch. */
  onCartUpdated?: () => void
}

// Same gate as room_controller.js: accept tracks from the LLM agent
// worker, the Simli avatar (joined under its own identity), or any future
// agent-prefixed worker. Everything else (the buyer themselves) is ignored
// so we never render our own loopback.
function isAgentSide(identity: string): boolean {
  return (
    identity === 'assistant-liveshop' ||
    identity === 'simli-avatar-agent' ||
    identity.startsWith('simli-') ||
    identity.startsWith('agent-')
  )
}

// Virtual / meeting-app mics that frequently surface as the system default
// but capture nothing useful. We skip these so the agent doesn't end up
// "listening" to a silent Teams/Zoom device. (Ported from room_controller.)
const SKIP_MIC_LABELS = [
  /virtual/i, /loopback/i, /teams/i, /zoom/i, /webex/i,
  /skype/i, /\bmeet\b/i, /screenflow/i, /soundflower/i, /blackhole/i,
]

// Choose a REAL microphone: prefer the OS default when it's a physical
// device, else the first non-virtual physical mic, else anything. Returns
// a deviceId to pin, or null to let the browser decide. enumerateDevices
// only returns real labels AFTER getUserMedia has been granted once.
async function pickBestMicDeviceId(): Promise<string | null> {
  if (!navigator.mediaDevices?.enumerateDevices) return null
  const inputs = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'audioinput')
  const isVirtual = (label: string) => SKIP_MIC_LABELS.some((re) => re.test(label))
  const isReal = (d: MediaDeviceInfo) => Boolean(d.label) && !isVirtual(d.label)

  // Honour the OS default if it's a real mic (not Teams); otherwise the
  // first real physical device; otherwise fall back to whatever exists.
  const osDefault = inputs.find((d) => d.deviceId === 'default' && isReal(d))
  const physical = inputs.find((d) => isReal(d) && d.deviceId !== 'default')
  const choice = osDefault || physical || inputs.find((d) => d.label) || inputs[0]
  return choice?.deviceId ?? null
}

const API_BASE = apiEndpoint.replace(/\/graphql\/?$/, '')

export function useVoiceAvatar({ productId, storeId, token, onCartUpdated }: Args): UseVoiceAvatar {
  const [phase, setPhase] = useState<AvatarPhase>('idle')
  const [assistantState, setAssistantState] = useState<AssistantState>('idle')
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([])
  const [micActive, setMicActive] = useState(false)
  const [hasVideo, setHasVideo] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const roomRef = useRef<Room | null>(null)
  const sessionIdRef = useRef<string | null>(null)
  const avatarRef = useRef<HTMLDivElement>(null)
  // Persisted mic track + chosen device, so PTT mutes/unmutes one track
  // instead of re-acquiring (which would re-prompt / re-pick the device).
  const micTrackRef = useRef<LocalTrack | null>(null)
  const micDeviceIdRef = useRef<string | null>(null)
  const micGrantedRef = useRef(false)
  // Mirror assistantState into a ref so the PTT handlers (stable
  // callbacks) can read the latest value without being re-created.
  const assistantStateRef = useRef<AssistantState>('idle')
  useEffect(() => { assistantStateRef.current = assistantState }, [assistantState])
  // Latest cart-refetch callback, read from the (stable) data handler.
  const onCartUpdatedRef = useRef(onCartUpdated)
  useEffect(() => { onCartUpdatedRef.current = onCartUpdated }, [onCartUpdated])

  const appendTranscript = useCallback((speaker: 'you' | 'assistant', text: string) => {
    if (!text) return
    setTranscript((prev) => [...prev, { speaker, text }])
  }, [])

  // Grant mic permission once (the browser then persists it per-origin)
  // and pick a real device. Safe to call repeatedly — it no-ops once
  // granted. Never throws; a denied mic just leaves the avatar watchable.
  const ensureMicReady = useCallback(async () => {
    if (micGrantedRef.current) return
    if (!navigator.mediaDevices?.getUserMedia) return
    try {
      // Temporary grab purely to (a) trigger/persist the permission and
      // (b) unlock real device labels. The long-lived track is opened by
      // LiveKit on the first PTT press.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((t) => t.stop())
      micGrantedRef.current = true
      micDeviceIdRef.current = await pickBestMicDeviceId()
    } catch {
      setError('Microphone access was blocked. Enable it in your browser to talk to the stylist.')
    }
  }, [])

  const wireRoomEvents = useCallback((room: Room) => {
    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (!isAgentSide(participant.identity)) return
      if (track.kind === Track.Kind.Video) {
        const el = track.attach()
        el.style.width = '100%'
        el.style.height = '100%'
        el.style.objectFit = 'cover'
        avatarRef.current?.replaceChildren(el)
        setHasVideo(true)
      } else if (track.kind === Track.Kind.Audio) {
        const audioEl = track.attach()
        audioEl.autoplay = true
        document.body.appendChild(audioEl) // detached audio sink
        // WebRTC audio is exempt from autoplay gating, but nudge it anyway.
        void audioEl.play().catch(() => {})
      }
    })

    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => track.detach())

    // Versioned data envelope router (demo spec §9).
    room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
      try {
        const envelope = JSON.parse(new TextDecoder().decode(payload))
        if (!envelope || envelope.version !== '1.0') return
        switch (envelope.type) {
          case 'assistant_state_changed':
            setAssistantState((envelope.payload?.state as AssistantState) || 'idle')
            break
          case 'buyer_transcript_final':
            appendTranscript('you', envelope.payload?.text || '')
            break
          case 'assistant_transcript_final':
            appendTranscript('assistant', envelope.payload?.text || '')
            break
          case 'cart_updated':
            // The agent changed the server-side basket — refetch the cart UI.
            onCartUpdatedRef.current?.()
            break
        }
      } catch {
        // Malformed envelope — ignore, never throw out of the handler.
      }
    })

    room.on(RoomEvent.Disconnected, () => {
      micTrackRef.current = null
      setAssistantState('idle')
      setMicActive(false)
      setHasVideo(false)
      setPhase((p) => (p === 'live' ? 'ended' : p))
    })
  }, [appendTranscript])

  const start = useCallback(async () => {
    if (roomRef.current) return
    setError(null)
    setPhase('connecting')
    // Prompt for the mic up front, inside the click gesture, so it's
    // granted once and the real device is chosen before the first PTT.
    // Don't block the session on it — the avatar is still watchable.
    void ensureMicReady()
    try {
      const body: Record<string, unknown> = { product_id: productId, store_id: storeId }
      if (!token) body.guest_identifier = getGuestId()

      const res = await fetch(`${API_BASE}/api/voice_sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}))
        throw new Error(detail.message || detail.error || `Session request failed (${res.status})`)
      }
      const { token: lkToken, ws_url, session } = await res.json()
      sessionIdRef.current = session?.id != null ? String(session.id) : null

      const room = new Room({ adaptiveStream: true, dynacast: true })
      wireRoomEvents(room)
      await room.connect(ws_url, lkToken)
      roomRef.current = room
      setAssistantState('idle')
      setPhase('live')
    } catch (err) {
      roomRef.current = null
      setError(err instanceof Error ? err.message : String(err))
      setPhase('error')
    }
  }, [productId, storeId, token, wireRoomEvents, ensureMicReady])

  const stop = useCallback(async () => {
    const room = roomRef.current
    roomRef.current = null
    micTrackRef.current = null
    if (room) room.disconnect()

    const sessionId = sessionIdRef.current
    if (sessionId) {
      const body: Record<string, unknown> = {}
      if (!token) body.guest_identifier = getGuestId()
      fetch(`${API_BASE}/api/voice_sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => { /* janitor will reap an orphan room anyway */ })
    }
    sessionIdRef.current = null
    setMicActive(false)
    setHasVideo(false)
    setPhase('ended')
  }, [token])

  const pttStart = useCallback(async () => {
    const room = roomRef.current
    if (!room) return
    // Don't open the mic while the avatar is mid-sentence — prevents the
    // assistant talking over itself (no barge-in).
    if (assistantStateRef.current === 'speaking') return
    await ensureMicReady()
    try {
      if (!micTrackRef.current) {
        // First press: acquire the real device once, then reuse it.
        const opts = micDeviceIdRef.current
          ? { deviceId: { exact: micDeviceIdRef.current } }
          : undefined
        await room.localParticipant.setMicrophoneEnabled(true, opts)
        micTrackRef.current = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track ?? null
      } else {
        // Subsequent presses: just unmute the persisted track (no re-acquire).
        await micTrackRef.current.unmute()
      }
      setMicActive(true)
      setAssistantState('listening')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Microphone permission denied.')
    }
  }, [ensureMicReady])

  const pttStop = useCallback(async () => {
    const track = micTrackRef.current
    if (!track) return
    // Mute (don't release) so the next press doesn't re-acquire / re-prompt.
    await track.mute()
    setMicActive(false)
    setAssistantState((s) => (s === 'listening' ? 'thinking' : s))
  }, [])

  // Teardown on unmount so navigating away never leaves a live room behind.
  useEffect(() => {
    return () => {
      const room = roomRef.current
      roomRef.current = null
      micTrackRef.current = null
      if (room) room.disconnect()
    }
  }, [])

  return { phase, assistantState, transcript, micActive, hasVideo, error, start, stop, pttStart, pttStop, avatarRef }
}
